import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from 'react';
import { createPortal } from 'react-dom';
import { applyThemeToDocument } from './ThemeContext';
import '../styles/ThemeSettingsModal.css';

// Default theme ID
const DEFAULT_THEME_ID = 'classic_light';

// Pre-designed themes like WordPress - easy for students to pick!
const THEME_GALLERY = [
  {
    id: 'classic_light',
    name: 'Classic Light',
    emoji: '📚',
    description: 'Clean and simple - perfect for studying',
    isDefault: true,
    colors: {
      primary: '#3b82f6',
      secondary: '#60a5fa',
      accent: '#2563eb',
      background: '#ffffff',
      card: '#f8fafc',
      text: '#1e293b',
    },
    preview: {
      header: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
      bg: '#ffffff',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#3b82f6',
      background_style: 'solid',
      theme_preset: 'classic_light',
    }
  },
  {
    id: 'ocean_breeze',
    name: 'Ocean Breeze',
    emoji: '🌊',
    description: 'Calm blue tones for focused studying',
    colors: {
      primary: '#0ea5e9',
      secondary: '#38bdf8',
      accent: '#06b6d4',
      background: '#f0f9ff',
      card: '#ffffff',
      text: '#0c4a6e',
    },
    preview: {
      header: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      bg: '#f0f9ff',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#0ea5e9',
      background_style: 'gradient',
      theme_preset: 'ocean_breeze',
    }
  },
  {
    id: 'sunset_glow',
    name: 'Sunset Glow',
    emoji: '🌅',
    description: 'Warm orange & pink vibes',
    colors: {
      primary: '#f97316',
      secondary: '#fb923c',
      accent: '#ec4899',
      background: '#fff7ed',
      card: '#ffffff',
      text: '#9a3412',
    },
    preview: {
      header: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
      bg: '#fff7ed',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#f97316',
      background_style: 'gradient',
      theme_preset: 'sunset_glow',
    }
  },
  {
    id: 'forest_green',
    name: 'Forest Green',
    emoji: '🌲',
    description: 'Natural green for relaxed learning',
    colors: {
      primary: '#22c55e',
      secondary: '#4ade80',
      accent: '#10b981',
      background: '#f0fdf4',
      card: '#ffffff',
      text: '#166534',
    },
    preview: {
      header: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
      bg: '#f0fdf4',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#22c55e',
      background_style: 'solid',
      theme_preset: 'forest_green',
    }
  },
  {
    id: 'lavender_dream',
    name: 'Lavender Dream',
    emoji: '💜',
    description: 'Soft purple for creative minds',
    colors: {
      primary: '#a855f7',
      secondary: '#c084fc',
      accent: '#8b5cf6',
      background: '#faf5ff',
      card: '#ffffff',
      text: '#6b21a8',
    },
    preview: {
      header: 'linear-gradient(135deg, #a855f7 0%, #8b5cf6 100%)',
      bg: '#faf5ff',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#a855f7',
      background_style: 'gradient',
      theme_preset: 'lavender_dream',
    }
  },
  {
    id: 'cherry_blossom',
    name: 'Cherry Blossom',
    emoji: '🌸',
    description: 'Sweet pink like spring flowers',
    colors: {
      primary: '#ec4899',
      secondary: '#f472b6',
      accent: '#db2777',
      background: '#fdf2f8',
      card: '#ffffff',
      text: '#9d174d',
    },
    preview: {
      header: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
      bg: '#fdf2f8',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#ec4899',
      background_style: 'solid',
      theme_preset: 'cherry_blossom',
    }
  },
  {
    id: 'sunny_day',
    name: 'Sunny Day',
    emoji: '☀️',
    description: 'Bright yellow for happy vibes',
    colors: {
      primary: '#eab308',
      secondary: '#facc15',
      accent: '#f59e0b',
      background: '#fefce8',
      card: '#ffffff',
      text: '#854d0e',
    },
    preview: {
      header: 'linear-gradient(135deg, #eab308 0%, #f59e0b 100%)',
      bg: '#fefce8',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#eab308',
      background_style: 'solid',
      theme_preset: 'sunny_day',
    }
  },
  {
    id: 'midnight_blue',
    name: 'Midnight Study',
    emoji: '🌙',
    description: 'Dark mode for night owls',
    colors: {
      primary: '#6366f1',
      secondary: '#818cf8',
      accent: '#8b5cf6',
      background: '#0f172a',
      card: '#1e293b',
      text: '#e2e8f0',
    },
    preview: {
      header: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      bg: '#0f172a',
    },
    settings: {
      theme_mode: 'dark',
      accent_color: '#6366f1',
      background_style: 'solid',
      theme_preset: 'midnight_blue',
    }
  },
  {
    id: 'galaxy_night',
    name: 'Galaxy Night',
    emoji: '🌌',
    description: 'Deep space dark theme',
    colors: {
      primary: '#8b5cf6',
      secondary: '#a78bfa',
      accent: '#c084fc',
      background: '#0c0a1d',
      card: '#1a1633',
      text: '#e9d5ff',
    },
    preview: {
      header: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
      bg: '#0c0a1d',
    },
    settings: {
      theme_mode: 'dark',
      accent_color: '#8b5cf6',
      background_style: 'gradient',
      theme_preset: 'galaxy_night',
    }
  },
  {
    id: 'mint_fresh',
    name: 'Mint Fresh',
    emoji: '🍃',
    description: 'Cool mint for fresh thinking',
    colors: {
      primary: '#14b8a6',
      secondary: '#2dd4bf',
      accent: '#0d9488',
      background: '#f0fdfa',
      card: '#ffffff',
      text: '#134e4a',
    },
    preview: {
      header: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)',
      bg: '#f0fdfa',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#14b8a6',
      background_style: 'solid',
      theme_preset: 'mint_fresh',
    }
  },
  {
    id: 'rose_gold',
    name: 'Rose Gold',
    emoji: '✨',
    description: 'Elegant rose tones',
    colors: {
      primary: '#e11d48',
      secondary: '#fb7185',
      accent: '#be123c',
      background: '#fff1f2',
      card: '#ffffff',
      text: '#881337',
    },
    preview: {
      header: 'linear-gradient(135deg, #e11d48 0%, #fb7185 100%)',
      bg: '#fff1f2',
    },
    settings: {
      theme_mode: 'light',
      accent_color: '#e11d48',
      background_style: 'gradient',
      theme_preset: 'rose_gold',
    }
  },
  {
    id: 'cozy_dark',
    name: 'Cozy Dark',
    emoji: '🛋️',
    description: 'Warm dark theme for comfort',
    colors: {
      primary: '#f59e0b',
      secondary: '#fbbf24',
      accent: '#d97706',
      background: '#1c1917',
      card: '#292524',
      text: '#fef3c7',
    },
    preview: {
      header: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
      bg: '#1c1917',
    },
    settings: {
      theme_mode: 'dark',
      accent_color: '#f59e0b',
      background_style: 'solid',
      theme_preset: 'cozy_dark',
    }
  },
];

const STRINGS = {
  title: 'Chọn giao diện',
  subtitle: 'Nhấn vào theme bạn thích để xem trước',
  pickTheme: 'Chọn một giao diện phù hợp với bạn',
  preview: 'Đang xem trước',
  apply: 'Áp dụng',
  cancel: 'Hủy',
  applied: 'Đã áp dụng!',
  currentTheme: 'Đang sử dụng',
};

const DEFAULT_SETTINGS = Object.freeze({
  theme_mode: 'light',
  accent_color: '#3b82f6',
  font_size: 'normal',
  background_style: 'solid',
  theme_preset: 'classic_light',
  mood: null,
  custom_background_url: null,
  custom_banner_url: null,
});

export default function ThemeSettingsModal({
  isOpen,
  onClose,
  initialSettings,
  onSave,
  onReset
}) {
  const modalId = useId();
  const titleId = `${modalId}-title`;

  const [selectedTheme, setSelectedTheme] = useState(null);
  const [previewTheme, setPreviewTheme] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Determine current theme from settings (use default if not set)
  const currentThemeId = useMemo(() => {
    if (!initialSettings?.theme_preset) return DEFAULT_THEME_ID;
    // Check if the preset exists in our gallery
    const exists = THEME_GALLERY.some(t => t.id === initialSettings.theme_preset);
    return exists ? initialSettings.theme_preset : DEFAULT_THEME_ID;
  }, [initialSettings]);

  // Focus management + body scroll lock
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement;

      // Lock body scroll so page behind is not scrollable
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        if (modalRef.current) {
          const firstFocus = modalRef.current.querySelector('[data-autofocus]') ||
              modalRef.current.querySelector('button');
          firstFocus && firstFocus.focus();
        }
      }, 10);

      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          handleClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = originalOverflow;
      };
    } else {
      if (previouslyFocusedRef.current?.focus) {
        previouslyFocusedRef.current.focus();
      }
    }
  }, [isOpen]);

  // Reset preview when modal closes
  useEffect(() => {
    if (!isOpen && previewTheme) {
      const currentTheme = THEME_GALLERY.find(t => t.id === currentThemeId);
      if (currentTheme && typeof applyThemeToDocument === 'function') {
        applyThemeToDocument(currentTheme.settings);
      }
      setPreviewTheme(null);
      setSelectedTheme(null);
    }
  }, [isOpen, currentThemeId]);

  const handleThemeClick = useCallback((theme) => {
    setSelectedTheme(theme.id);
    setPreviewTheme(theme.id);

    // Apply preview immediately
    if (typeof applyThemeToDocument === 'function') {
      applyThemeToDocument(theme.settings);
    }
  }, []);

  const handleApply = useCallback(async () => {
    if (!selectedTheme || !onSave) return;

    const theme = THEME_GALLERY.find(t => t.id === selectedTheme);
    if (!theme) return;

    setSaving(true);
    try {
      await onSave(theme.settings);
      setSaved(true);
      fireConfetti();
      setTimeout(() => {
        setSaved(false);
        onClose?.();
      }, 1000);
    } catch (e) {
      console.error(e);
      alert('Không thể lưu. Vui lòng thử lại.');
    } finally {
      setSaving(false);
    }
  }, [selectedTheme, onSave, onClose]);

  const handleClose = useCallback(() => {
    // Revert to current theme if previewing
    if (previewTheme && previewTheme !== currentThemeId) {
      const currentTheme = THEME_GALLERY.find(t => t.id === currentThemeId);
      if (currentTheme && typeof applyThemeToDocument === 'function') {
        applyThemeToDocument(currentTheme.settings);
      }
    }
    setPreviewTheme(null);
    setSelectedTheme(null);
    onClose?.();
  }, [previewTheme, currentThemeId, onClose]);

  if (!isOpen) return null;

  // Use createPortal to render at document.body level,
  // bypassing any parent stacking context (transform, filter, etc.)
  return createPortal(
    <div
      className="theme-gallery-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="theme-gallery-modal"
      >
        {/* Header */}
        <div className="theme-gallery-header">
          <div className="theme-gallery-header-content">
            <div className="theme-gallery-icon">🎨</div>
            <div>
              <h2 id={titleId} className="theme-gallery-title">{STRINGS.title}</h2>
              <p className="theme-gallery-subtitle">{STRINGS.subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            className="theme-gallery-close"
            aria-label="Đóng"
            onClick={handleClose}
            data-autofocus
          >
            ✕
          </button>
        </div>

        {/* Theme Grid */}
        <div className="theme-gallery-content">
          <div className="theme-gallery-grid">
            {THEME_GALLERY.map((theme) => {
              const isCurrent = currentThemeId === theme.id;
              const isSelected = selectedTheme === theme.id;
              const isPreviewing = previewTheme === theme.id;

              return (
                <button
                  key={theme.id}
                  type="button"
                  className={`theme-card ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
                  onClick={() => handleThemeClick(theme)}
                  aria-pressed={isSelected}
                >
                  {/* Theme Preview */}
                  <div
                    className="theme-card-preview"
                    style={{ background: theme.preview.bg }}
                  >
                    <div
                      className="theme-card-header"
                      style={{ background: theme.preview.header }}
                    >
                      <div className="theme-card-header-dot"></div>
                      <div className="theme-card-header-dot"></div>
                      <div className="theme-card-header-dot"></div>
                    </div>
                    <div className="theme-card-body">
                      <div
                        className="theme-card-sidebar"
                        style={{ background: theme.colors.card }}
                      ></div>
                      <div className="theme-card-content">
                        <div
                          className="theme-card-line"
                          style={{ background: theme.colors.primary }}
                        ></div>
                        <div
                          className="theme-card-line short"
                          style={{ background: theme.colors.secondary, opacity: 0.6 }}
                        ></div>
                        <div
                          className="theme-card-btn"
                          style={{ background: theme.colors.primary }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Theme Info */}
                  <div className="theme-card-info">
                    <div className="theme-card-emoji">{theme.emoji}</div>
                    <div className="theme-card-details">
                      <div className="theme-card-name">{theme.name}</div>
                      <div className="theme-card-desc">{theme.description}</div>
                    </div>
                  </div>

                  {/* Badges */}
                  {theme.isDefault && !isCurrent && !isSelected && (
                    <div className="theme-card-badge default-badge">
                      ⭐ Mặc định
                    </div>
                  )}
                  {isCurrent && !isSelected && (
                    <div className="theme-card-badge current-badge">
                      ✓ {STRINGS.currentTheme}
                    </div>
                  )}
                  {isPreviewing && (
                    <div className="theme-card-badge preview-badge">
                      👁 {STRINGS.preview}
                    </div>
                  )}
                  {isSelected && (
                    <div className="theme-card-check">✓</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="theme-gallery-footer">
          <button
            type="button"
            className="theme-gallery-btn secondary"
            onClick={handleClose}
          >
            {STRINGS.cancel}
          </button>
          <button
            type="button"
            className={`theme-gallery-btn primary ${saved ? 'saved' : ''}`}
            onClick={handleApply}
            disabled={!selectedTheme || saving || saved}
          >
            {saved ? `✓ ${STRINGS.applied}` : saving ? 'Đang lưu...' : STRINGS.apply}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Confetti animation
function fireConfetti() {
  if (typeof document === 'undefined') return;
  try {
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden;';
    document.body.appendChild(container);

    const colors = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];
    const emojis = ['🎉', '✨', '🎊', '⭐', '💜', '💙'];

    for (let i = 0; i < 50; i++) {
      const el = document.createElement('div');
      const isEmoji = Math.random() > 0.5;

      if (isEmoji) {
        el.textContent = emojis[Math.floor(Math.random() * emojis.length)];
        el.style.fontSize = (16 + Math.random() * 16) + 'px';
      } else {
        el.style.width = (8 + Math.random() * 8) + 'px';
        el.style.height = el.style.width;
        el.style.background = colors[Math.floor(Math.random() * colors.length)];
        el.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';
      }

      el.style.cssText += `
        position:absolute;
        left:${Math.random() * 100}%;
        top:-20px;
        animation:confetti-fall ${2 + Math.random()}s ease-out forwards;
        transform:rotate(${Math.random() * 360}deg);
      `;
      container.appendChild(el);
    }

    const style = document.createElement('style');
    style.textContent = `
      @keyframes confetti-fall {
        to { transform: translateY(100vh) rotate(720deg); opacity: 0; }
      }
    `;
    document.head.appendChild(style);

    setTimeout(() => {
      container.remove();
      style.remove();
    }, 3000);
  } catch (e) {}
}
